import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { accountList } from "app/services/transmission/account";
import { hospitalList } from "app/services/transmission/hospital";
import {
  createPatient,
  fetchPatientById,
  fetchPatientLeadInformation,
  updateBasicInformation,
  updateBillingSchedule,
  updateHeartFailure,
  updateMultipleBillingSchedule,
  updatePatientAlertBanner,
  updatePatientDeviceInformation,
  updatePatientInformation,
  updatePatientInsurance,
  updatePatientLeadInformation,
  updatePatientTask,
  updatePatientTransmission,
  updateSubscribeSetting,
} from "app/services/transmission/patient";
import {
  physicianList,
  updatePhysicianData,
} from "app/services/transmission/physician";

import {
  assignPhysicianToReport,
  billBulkReports,
  bulkArchiveReports,
  changeNextSend,
  downloadBulkReports,
  getPatientReports,
  patientAlertBanner,
  processBulkReports,
  reportsCount,
  signBulkReports,
  unsignBulkReports,
  updateNextSend,
} from "app/services/transmission/report";
import { processBulkHl7Jobs } from "app/services/transmission/hl7Job";
import { BASE_URL } from "static/constants";
import formatDate from "utils/formatDate";
import { transmissionDetailApi } from "app/store/transmission/api/transmissionDetailApi";

export const transmissionTableApi = createApi({
  reducerPath: "transmissionTableApi",
  baseQuery: fetchBaseQuery({ baseUrl: BASE_URL }),
  tagTypes: [
    "transmissions",
    "transmissionsCount",
    "patientProfile",
    "getAlertBanner",
    "patientLeadInformation",
    "physicians",
    "accounts",
  ],
  endpoints: ({ query, mutation }) => ({
    getReports: query({
      queryFn: ({ url }, { signal }) => {
        return { data: getPatientReports({ url, signal }) };
      },
      providesTags: (_result, _error, arg) => ["transmissions", arg?.view],
    }),
    getReportsCount: query({
      queryFn: ({
        year,
        physicians,
        hospitals,
        isMyPatientsFilter,
        patch,
      }) => {
        return {
          data: reportsCount({
            year,
            physicians,
            hospitals,
            isMyPatientsFilter,
            patch,
          }),
        };
      },

      providesTags: ["transmissionsCount"],
    }),

    updateNext: mutation({
      queryFn: ({ patientId, nextSendDate }) => {
        return { data: changeNextSend(patientId, nextSendDate) };
      },
    }),

    updateNextSend: mutation({
      queryFn: (payload) => {
        return { data: updateNextSend(payload) };
      },
      async onQueryStarted({ view }, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        await dispatch(
          transmissionTableApi.util.invalidateTags(["transmissions"])
        );
        await dispatch(
          transmissionTableApi.util.invalidateTags(["transmissionsCount"])
        );
      },
    }),

    bulkDownload: mutation({
      queryFn: (reports) => {
        return downloadBulkReports(reports).then(async (response) => {
          const date = formatDate(new Date(), "YYYY-MM-DD");
          var fileName = `evoke-reports-${date}`;
          var a = document.createElement("a");

          const file = new Blob([response.data], {
            type: "application/zip",
          });
          const fileURL = URL.createObjectURL(file);

          document.body.appendChild(a);
          a.style = "display:none";

          a.href = fileURL;
          a.download = fileName;
          a.click();

          a.remove();
          return { data: null };
        });
      },
    }),
    processReports: mutation({
      queryFn: ({
        reports,
        view,
        processModalState,
        toReturn,
        isSign,
        isClinicProcessedUnprocess,
        shouldSendLetter,
        isArchive,
      }) => {
        return {
          data: processBulkReports(
            reports,
            view,
            processModalState,
            toReturn,
            isSign,
            isClinicProcessedUnprocess,
            shouldSendLetter,
            isArchive
          ),
        };
      },
      async onQueryStarted({ view }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          await dispatch(
            transmissionTableApi.util.invalidateTags(["transmissions", view])
          );
          await dispatch(
            transmissionTableApi.util.invalidateTags([
              "transmissionsCount",
              view,
            ])
          );
        } catch (err) {
          console.error("Error invalidating tags:", err);
        }
      },
    }),
    archiveReports: mutation({
      queryFn: ({ reports, processModalState, isUndo }) => {
        return {
          data: bulkArchiveReports(reports, processModalState, isUndo),
        };
      },

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        await dispatch(
          transmissionTableApi.util.invalidateTags(["transmissions"])
        );
        await dispatch(
          transmissionTableApi.util.invalidateTags(["transmissionsCount"])
        );
      },
    }),
    billReports: mutation({
      queryFn: (reports) => {
        return { data: billBulkReports(reports) };
      },
      invalidatesTags: ["transmissions", "transmissionsCount"],
    }),
    signReports: mutation({
      queryFn: (reports) => {
        return { data: signBulkReports(reports) };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        await dispatch(
          transmissionTableApi.util.invalidateTags(["transmissions"])
        );
        await dispatch(
          transmissionTableApi.util.invalidateTags(["transmissionsCount"])
        );
      },
    }),
    unsignReports: mutation({
      queryFn: (reports) => {
        return { data: unsignBulkReports(reports) };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(transmissionDetailApi.util.invalidateTags(["reportDetail"]));
        await dispatch(
          transmissionTableApi.util.invalidateTags(["transmissions"])
        );
        await dispatch(
          transmissionTableApi.util.invalidateTags(["transmissionsCount"])
        );
      },
    }),
    getPhysicians: query({
      queryFn: ({ isPhysicianModal, ...queryParams }) => {
        return {
          data: physicianList(isPhysicianModal, queryParams),
        };
      },
      providesTags: ["physicians"],
    }),
    updatePhysicians: mutation({
      queryFn: ({ physicians, patientSerialNumber }) => {
        return { data: updatePhysicianData(physicians, patientSerialNumber) };
      },
      invalidatesTags: ["transmissions", "transmissionsCount"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(transmissionDetailApi.util.invalidateTags(["reportDetail"]));
      },
    }),
    getHospitals: query({
      queryFn: () => {
        return {
          data: hospitalList(),
        };
      },
    }),
    getPatient: query({
      queryFn: async ({ patientId, isPatientModal }) => {
        const data = await fetchPatientById(patientId, isPatientModal);
        return { data };
      },
      providesTags: (_result, _error, arg) => [
        "patientProfile",
        arg?.patientId,
      ],

      keepUnusedDataFor: 0,
    }),
    getAccounts: query({
      queryFn: (isPatientModal) => {
        return accountList(isPatientModal);
      },
      providesTags: ["accounts"],
    }),
    saveBasicInformation: mutation({
      queryFn: ({ serialNumber, profile, patientAlertInformation }) => {
        return {
          data: updateBasicInformation(
            serialNumber,
            profile,
            patientAlertInformation
          ),
        };
      },
      providesTags: (_result, _error, arg) => [
        "patientProfile",
        arg?.serialNumber,
      ],
      invalidatesTags: ["transmissions", "transmissionsCount"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(transmissionDetailApi.util.invalidateTags(["reportDetail"]));
      },
    }),
    saveTransmission: mutation({
      queryFn: ({ serialNumber, profile }) => {
        return {
          data: updatePatientTransmission(serialNumber, profile),
        };
      },
      invalidatesTags: ["transmissions", "transmissionsCount"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(transmissionDetailApi.util.invalidateTags(["reportDetail"]));
      },
    }),
    saveBillingSchedule: mutation({
      queryFn: ({ patientId, days, active, billingCode }) => {
        return {
          data: updateBillingSchedule(patientId, days, active, billingCode),
        };
      },
      invalidatesTags: ["transmissions", "transmissionsCount"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(transmissionDetailApi.util.invalidateTags(["reportDetail"]));
      },
    }),
    saveMultipleBillingSchedule: mutation({
      queryFn: (payload) => {
        return {
          data: updateMultipleBillingSchedule(payload),
        };
      },
    }),
    saveInformation: mutation({
      queryFn: ({ serialNumber, information }) => {
        return {
          data: updatePatientInformation(serialNumber, information),
        };
      },
      invalidatesTags: ["transmissions", "transmissionsCount"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(transmissionDetailApi.util.invalidateTags(["reportDetail"]));
      },
    }),
    saveDeviceInformation: mutation({
      queryFn: (payload) => {
        return {
          data: updatePatientDeviceInformation(payload),
        };
      },
      invalidatesTags: [
        "transmissions",
        "transmissionsCount",
        "getAlertBanner",
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;

        dispatch(transmissionDetailApi.util.invalidateTags(["reportDetail"]));
      },
    }),
    getPatientLeadInformation: query({
      queryFn: async ({ patientId }) => {
        const response = await fetchPatientLeadInformation(patientId);
        return { data: response };
      },
      providesTags: (_result, _error, arg) => [
        "patientLeadInformation",
        arg?.patientId,
      ],
    }),

    updatePatientLeadInformation: mutation({
      queryFn: ({ patientId, leads }) => {
        return {
          data: updatePatientLeadInformation(patientId, leads),
        };
      },
      invalidatesTags: (_types, _result, arg) => [
        "patientLeadInformation",
        "getAlertBanner",
        arg?.patientId,
      ],
    }),
    saveInsurance: mutation({
      queryFn: ({ serialNumber, insurance, type }) => {
        return {
          data: updatePatientInsurance(serialNumber, insurance, type),
        };
      },
    }),
    savePatientTask: mutation({
      queryFn: ({ patientId, task, isEditTask }) => {
        return {
          data: updatePatientTask(patientId, task, isEditTask),
        };
      },
    }),
    getAlertBanner: query({
      queryFn: (serialNumber) => {
        return {
          data: patientAlertBanner(serialNumber),
        };
      },
      providesTags: (_result, _error, serialNumber) => [
        "getAlertBanner",
        serialNumber,
      ],
      keepUnusedDataFor: 0,
    }),
    saveAlertBanner: mutation({
      queryFn: ({ serialNumber, bannerText }) => {
        return {
          data: updatePatientAlertBanner(serialNumber, bannerText),
        };
      },
      invalidatesTags: (_result, _error, { serialNumber }) => [
        "getAlertBanner",
        serialNumber,
      ],
    }),
    saveHeartFailure: mutation({
      queryFn: (serialNumber) => {
        return {
          data: updateHeartFailure(serialNumber),
        };
      },
      invalidatesTags: (serialNumber) => ["patientProfile", serialNumber],
    }),
    createPatient: mutation({
      queryFn: ({ serialNumber, modelName, modelNumber, deviceType }) => {
        return {
          data: createPatient({
            serialNumber,
            modelName,
            modelNumber,
            deviceType,
          }),
        };
      },
      invalidatesTags: (result, _error, _arg) => [
        "patientLeadInformation",
        result?.data?.oldPatient?._id,
      ],
    }),
    updatePatientSubscription: mutation({
      queryFn: ({ patientId, subscribeSetting }) => {
        return {
          data: updateSubscribeSetting(patientId, subscribeSetting),
        };
      },
    }),
    processBulkHl7Jobs: mutation({
      queryFn: (jobs) => {
        return {
          data: processBulkHl7Jobs(jobs),
        };
      },
    }),
    assignPhysicianToReport: mutation({
      queryFn: ({ reportId, physicianId, assignToPatient }) => {
        return {
          data: assignPhysicianToReport(reportId, physicianId, assignToPatient),
        };
      },
      invalidatesTags: ["transmissions", "transmissionsCount"],
    }),
  }),
});

export const {
  useGetReportsQuery,
  useGetPatientQuery,
  useGetPhysiciansQuery,
  useGetReportsCountQuery,
  useGetAccountsQuery,
  useUpdateNextMutation,
  useUpdatePhysiciansMutation,
  useBulkDownloadMutation,
  useProcessReportsMutation,
  useBillReportsMutation,
  useSignReportsMutation,
  useUnsignReportsMutation,
  useSaveBasicInformationMutation,
  useSaveTransmissionMutation,
  useSaveBillingScheduleMutation,
  useSaveMultipleBillingScheduleMutation,
  useSaveInformationMutation,
  useSaveDeviceInformationMutation,
  useSaveInsuranceMutation,
  useSavePatientTaskMutation,
  useSaveAlertBannerMutation,
  useSaveHeartFailureMutation,
  useUpdateNextSendMutation,
  useGetAlertBannerQuery,
  useCreatePatientMutation,
  useUpdatePatientSubscriptionMutation,
  useGetPatientLeadInformationQuery,
  useUpdatePatientLeadInformationMutation,
  useArchiveReportsMutation,
  useProcessBulkHl7JobsMutation,
  useGetHospitalsQuery,
  useAssignPhysicianToReportMutation,
} = transmissionTableApi;
